/**
 * 대시보드 집계. 화면도 저장소도 모르는 순수 함수 하나로 둔다 —
 * 「이달」·「7일」 같은 기준은 눈으로 검증할 수 없어서 테스트가 붙어야 하고,
 * 테스트가 붙으려면 Next 런타임 밖에서 부를 수 있어야 한다.
 *
 * 기준 시각(now)은 인자로 받는다. 함수 안에서 Date.now() 를 부르면
 * 「이달 경계」 를 테스트할 방법이 없다.
 *
 * 「이달」 의 경계는 **Asia/Seoul 고정**이다. 배포 서버 TZ(Vercel 기본 UTC)를 따르면
 * KST 1일 새벽에 발송·결정된 건이 전달로 집계되어, 같은 값을 `formatDate`(KST)로
 * 찍는 목록과 숫자 카드가 어긋난다.
 *
 * 세는 단위는 견적 행이 아니라 **`quote_no`** 다. 새 버전을 뜨면 행이 하나 늘 뿐
 * 이전 버전의 상태가 닫히지 않아서, 행으로 세면 한 건이 두 건으로 보인다.
 */

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { APP_TZ } from "../../lib/format";
import { Customer } from "../customer/types";
import { Quote } from "./types";

dayjs.extend(utc);
dayjs.extend(timezone);

/** 발송하고 이만큼 지나도록 답이 없으면 챙겨야 할 건으로 본다. */
export const WAITING_DAYS = 7;

/** 최근 고객 표에 올리는 수. */
export const RECENT_CUSTOMER_LIMIT = 5;

export interface DashboardStats {
  /** 이달에 발송된 견적 건수(sent_at 기준, `quote_no` 하나당 1). */
  sent_this_month: number;
  /** 지금 답을 기다리는 전체 — SENT + NEGOTIATING (`quote_no` 하나당 1). */
  in_progress: number;
  // in_progress 중 최신 열린 버전이 발송(SENT)인 건 — 견적 목록 「발송」 칩과 같은 이름.
  sent_open: number;
  // in_progress 중 최신 열린 버전이 조율 중(NEGOTIATING)인 건 — 목록 「조율 중」 칩과 같은 이름.
  negotiating: number;
  // 견적 수주율(0~1) — 발송 이력이 있는 계열(기간 무관) 중 수주로 결정된 비율. 보낸 게 없으면 null.
  win_rate: number | null;
  // win_rate 의 분모 — 한 번이라도 발송된 상세견적 계열 수.
  sent_series: number;
  /** 이달 수주된 견적 금액 합계. */
  won_amount_this_month: number;
}

/** 「답 기다리는 견적」 한 줄. 열람 여부는 공유링크에 있어 화면에서 붙인다. */
export interface WaitingQuote {
  id: string;
  quote_no: string;
  version: number;
  customer_id: string;
  customer_name: string;
  total: number;
  sent_at: string;
  /** 발송 후 지난 날수(내림). */
  days_waiting: number;
}

export interface RecentCustomer {
  id: string;
  name: string;
  stage: Customer["stage"];
  site_address: string;
  created_at: string;
}

export interface Dashboard {
  stats: DashboardStats;
  waiting: WaitingQuote[];
  recent_customers: RecentCustomer[];
}

// 계열 판정이 읽는 열 — 유입 분석이 좁혀 읽은 견적(BoardQuote)도 같은 함수로 센다.
export type SeriesQuote = Pick<Quote, "quote_no" | "kind" | "version" | "status" | "sent_at">;

/** 답을 기다리는 상태. 두 곳에서 같은 판단을 하지 않도록 한 군데 둔다. */
const isOpen = (quote: SeriesQuote): boolean =>
  quote.status === "SENT" || quote.status === "NEGOTIATING";

const isDecided = (quote: SeriesQuote): boolean =>
  quote.status === "WON" || quote.status === "LOST";

/** KST 기준 `YYYY-MM`. 두 값을 같은 자로 재야 경계가 흔들리지 않는다. */
const monthKey = (value: dayjs.Dayjs): string =>
  value.tz(APP_TZ).format("YYYY-MM");

const isSameMonth = (value: string | null, nowMonth: string): boolean => {
  if (!value) return false;
  const at = dayjs(value);
  return at.isValid() && monthKey(at) === nowMonth;
};

const daysBetween = (from: string, now: dayjs.Dayjs): number => {
  const at = dayjs(from);
  return at.isValid() ? now.diff(at, "day") : 0;
};

/** 버전이 큰 것이 최신. 같은 버전이 둘일 수 없으니 그 이상은 보지 않는다. */
const latestOf = <T extends SeriesQuote>(quotes: T[]): T | null =>
  quotes.reduce<T | null>(
    (best, q) => (best === null || q.version > best.version ? q : best),
    null
  );

/**
 * `quote_no` 하나를 어떻게 셀지 정해 둔 묶음.
 *
 * 열려 있는지는 **최신 버전이 결정됐는지**로 본다 — v1 SENT 인 채 v2 가 수주되면
 * 그 건은 닫힌 것이다.
 *
 * 반대 방향(확정된 계열에 새 발송이 붙는 것)은 지금 저장소가 아예 막는다 —
 * `sendQuote` 가 계열에 확정본이 있으면 거부한다. 여기 비교는 그 규칙이 없던
 * 시절의 자료와 손으로 고친 상태까지 받아 내는 **바닥**이다.
 */
export interface SeriesState<T extends SeriesQuote = Quote> {
  /** 열려 있으면 「협의 중」 1건. */
  is_open: boolean;
  // 열려 있을 때 최신 열린 버전 한 줄 — 「발송」·「조율 중」 카드와 견적 목록 ?latest=1 이 이 줄을 센다.
  open_head: T | null;
  // 열려 있을 때 최신 열린 버전의 상태(SENT·NEGOTIATING) — 닫혔으면 null.
  open_status: T["status"] | null;
  /** 열려 있는 버전 중 발송된 최신본. 「답 기다리는 견적」 한 줄이 된다. */
  waiting_on: T | null;
  /** 이 건의 결정(있으면 최신 결정본 하나). 수주율·수주 금액은 이것만 본다. */
  decision: T | null;
}

interface QuoteGroup extends SeriesState {
  /** 버전 어느 하나라도 이달에 발송됐나. */
  sent_this_month: boolean;
  // 버전 어느 하나라도 발송된 적이 있나 — 견적 수주율의 분모.
  ever_sent: boolean;
}

// 상세견적을 quote_no 계열로 묶는다 — 대시보드·유입 분석이 한 벌로 쓴다.
export const quoteSeries = <T extends SeriesQuote>(quotes: readonly T[]): T[][] => {
  const byNo = new Map<string, T[]>();
  for (const quote of quotes) {
    /**
     * **가견적은 세지 않는다.**
     *
     * 가견적은 「대략 얼마인가」 한 번 답하고 끝나는 선택 단계다. 상세견적과
     * 다른 건 번호로 만들어지고 상세견적으로 이어지는 길도 없어서, 그대로 세면
     * 네 칸이 전부 틀어진다 — 고객 한 명에게 발송 2건, 「답 기다리는 견적」에
     * 영영 남는 가견적 1건, 그것을 치우려고 수주로 닫으면 수주 금액이 두 배,
     * 이탈로 닫으면 수주율이 절반.
     *
     * 대시보드가 답하는 질문은 「답 줘야 할 견적이 뭔지 · 이번 달 얼마 따냈는지」
     * 다(제품 원칙(옛 3절)). 개산은 그 둘 어디에도 해당하지 않는다.
     */
    if (quote.kind === "ROUGH") continue;

    const rows = byNo.get(quote.quote_no);
    if (rows) rows.push(quote);
    else byNo.set(quote.quote_no, [quote]);
  }

  return Array.from(byNo.values());
};

// 계열 하나의 열림·결정 — 최신 결정본보다 새 발송본이 있으면 열린 것이다.
export const seriesStateOf = <T extends SeriesQuote>(rows: T[]): SeriesState<T> => {
  const open = rows.filter(isOpen);
  const latestOpen = latestOf(open);
  const latestDecided = latestOf(rows.filter(isDecided));

  const isGroupOpen =
    latestOpen !== null &&
    (latestDecided === null || latestOpen.version > latestDecided.version);

  const head = isGroupOpen ? latestOpen : null;
  return {
    is_open: isGroupOpen,
    open_head: head,
    open_status: head ? head.status : null,
    waiting_on: isGroupOpen ? latestOf(open.filter((q) => q.sent_at)) : null,
    decision: latestDecided,
  };
};

// 열린 계열마다 최신 열린 버전 한 줄(가견적 제외) — 대시보드 카드와 견적 목록 「계열 최신」 필터가 같은 함수를 쓴다.
export const openSeriesHeads = <T extends Quote>(quotes: readonly T[]): T[] =>
  quoteSeries([...quotes]).flatMap((rows) => {
    const head = seriesStateOf(rows).open_head;
    return head ? [head as T] : [];
  });

const groupsOf = (quotes: Quote[], nowMonth: string): QuoteGroup[] =>
  quoteSeries(quotes).map((rows): QuoteGroup => ({
    ...seriesStateOf(rows),
    sent_this_month: rows.some((q) => isSameMonth(q.sent_at, nowMonth)),
    ever_sent: rows.some((q) => q.sent_at !== null),
  }));

export const buildDashboard = (
  quotes: Quote[],
  customers: Customer[],
  now: Date | string
): Dashboard => {
  const at = dayjs(now);
  const nowMonth = monthKey(at);
  const customerName = new Map(customers.map((c) => [c.id, c.name]));

  const groups = groupsOf(quotes, nowMonth);

  const decidedThisMonth = groups
    .map((group) => group.decision)
    .filter(
      (quote): quote is Quote =>
        quote !== null && isSameMonth(quote.decided_at, nowMonth)
    );
  const won = decidedThisMonth.filter((q) => q.status === "WON");
  const heads = openSeriesHeads(quotes);
  const sentGroups = groups.filter((group) => group.ever_sent);
  const wonSent = sentGroups.filter((group) => group.decision?.status === "WON");

  const stats: DashboardStats = {
    sent_this_month: groups.filter((group) => group.sent_this_month).length,
    in_progress: groups.filter((group) => group.is_open).length,
    sent_open: heads.filter((q) => q.status === "SENT").length,
    negotiating: heads.filter((q) => q.status === "NEGOTIATING").length,
    // 0/0 은 0% 가 아니다 — 아직 보낸 견적이 없다는 뜻이라 null 로 둔다.
    win_rate: sentGroups.length ? wonSent.length / sentGroups.length : null,
    sent_series: sentGroups.length,
    won_amount_this_month: won.reduce((sum, q) => sum + q.total, 0),
  };

  const waiting: WaitingQuote[] = groups
    .map((group) => group.waiting_on)
    .filter((quote): quote is Quote => quote !== null)
    .map((q): WaitingQuote => {
      const sentAt = q.sent_at as string;
      return {
        id: q.id,
        quote_no: q.quote_no,
        version: q.version,
        customer_id: q.customer_id,
        customer_name: customerName.get(q.customer_id) ?? "",
        total: q.total,
        sent_at: sentAt,
        days_waiting: daysBetween(sentAt, at),
      };
    })
    .filter((row) => row.days_waiting >= WAITING_DAYS)
    // 오래 기다린 것이 위로. 챙길 순서가 곧 정렬이다.
    .sort((a, b) => b.days_waiting - a.days_waiting);

  const recent_customers: RecentCustomer[] = customers
    .slice()
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, RECENT_CUSTOMER_LIMIT)
    .map((c) => ({
      id: c.id,
      name: c.name,
      stage: c.stage,
      site_address: c.site_address,
      created_at: c.created_at,
    }));

  return { stats, waiting, recent_customers };
};

/** 0.6 → "60%". 결정된 게 없으면 자리표시를 그대로 돌려준다. */
export const formatWinRate = (
  rate: number | null,
  fallback: string
): string => {
  if (rate === null) return fallback;
  const pct = rate * 100;
  return `${Number.isInteger(pct) ? pct : Number(pct.toFixed(1))}%`;
};
